import type { Shift } from '../types';

const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/**
 * Returns the id of the shift whose time window contains the provided `now` time.
 * Supports overnight shifts (e.g. 22:00–02:00) and end-of-day `"24:00"`.
 */
export const getShiftForCurrentTime = (
  shifts: Shift[],
  now: Date = new Date(),
): string | undefined => {
  if (!shifts.length) return undefined;

  const minutesNow = now.getHours() * 60 + now.getMinutes();

  for (const shift of shifts) {
    const start = toMinutes(shift.start_time);
    let end = toMinutes(shift.end_time);

    // Normalize end for overnight and 24:00 semantics
    if (shift.end_time === '24:00') {
      end = 24 * 60;
    } else if (end <= start) {
      end += 24 * 60;
    }

    let target = minutesNow;
    if (target < start) {
      target += 24 * 60;
    }

    if (target >= start && target < end) {
      return String(shift.id);
    }
  }

  return undefined;
};

/** Display order for shift pickers: Πρωινή first, Βραδινή second, then others by start time. */
export const sortActiveShiftsForDisplay = (shifts: Shift[]): Shift[] => {
  const active = shifts.filter((s) => s.is_active);
  const rank = (s: Shift): number => {
    const n = s.name.trim().toLowerCase();
    if (n.includes('πρωιν') || n.includes('morning')) return 0;
    if (n.includes('βραδ') || n.includes('evening') || n.includes('night')) return 1;
    return 2;
  };
  return [...active].sort((a, b) => {
    const dr = rank(a) - rank(b);
    if (dr !== 0) return dr;
    return toMinutes(a.start_time) - toMinutes(b.start_time);
  });
};

